import { useEffect, useRef, useState } from "react";
import { C, fmtChips } from "../../lib/api";
import { liftKeyOf, orderHand, presentationsOf } from "../../lib/showdown";
import { chipAssetId } from "../../assets/assetManifest";
import { preloadSounds, sounds } from "../../lib/sounds";
import { AssetImage } from "../AssetImage";
import { BetChips, PotChips, chipStack } from "./BetChips";
import { CardView } from "./CardView";
import { DealerPortrait } from "./Dealer";
import { WinnerPills } from "./ResultBanner";
import { SeatView } from "./SeatView";
import { ChatBubbles } from "./ChatBubbles";
import { BombPotBurst } from "./BombPotBurst";

// Table art is 1200x600 (2:1); dealer notch top-centre (x 36–64%), chip rack fills the notch down to ~y 35%.
// Screen slots run clockwise from the dealer's left (top-right) round to top-left; `me` is the slot the viewer is
// rotated into (bottom-right, beside the action bar — "Seat 3" on the 6-max). Spectators see engine seat i in slot i.
// Seats sit on the outside of the rail: roughly 10% of the avatar overlaps the table edge (top seats lift ~7%, bottom seats drop ~8%).
const out = ([x, y]) => [x < 10 ? x - 0.5 : x > 90 ? x + 0.5 : x, y < 20 ? y - 4 : y > 85 ? y + 8 : y];
const RAW_LAYOUTS = {
  2: { slots: [[80, 7], [70, 95]], me: 1 },
  3: { slots: [[80, 7], [70, 95], [20, 7]], me: 1 },
  4: { slots: [[80, 7], [98, 52], [70, 95], [2, 52]], me: 2 },
  5: { slots: [[78, 8], [98, 55], [50, 96], [2, 55], [22, 8]], me: 2 },
  6: { slots: [[80, 7], [98, 52], [70, 95], [30, 95], [2, 52], [20, 7]], me: 2 },
  7: { slots: [[80, 7], [98, 45], [85, 88], [55, 97], [25, 95], [2, 45], [20, 7]], me: 2 },
  8: { slots: [[80, 7], [98, 40], [95, 78], [65, 97], [35, 97], [5, 78], [2, 40], [20, 7]], me: 3 },
  9: { slots: [[76, 6], [97, 32], [97, 68], [80, 94], [50, 98], [20, 94], [3, 68], [3, 32], [24, 6]], me: 3 },
};
const LAYOUTS = Object.fromEntries(Object.entries(RAW_LAYOUTS).map(([n, l]) => [n, { ...l, slots: l.slots.map(out) }]));

export const useFelt = () => localStorage.getItem("pfp_felt") || "table_felt_green";

const DEALER_AT = [50, 4]; // where cards fly from (% of table)
const POT_AT = [50, 32]; // pot plate centre (% of table), sitting just over the lip of the chip tray — bets are swept here at the end of a street
const BURST_MS = 4200; // BOMB POT burst: spins in, bounces twice, holds, fades
// where a seat's chips land: just inside the betting line (the gold line on the felt runs ≈ x 18–81%, y 28–70% of the table box).
// Side seats land level with the line's edge beside them; top/bottom seats just inside its top/bottom edge, nudged toward the middle.
const betPos = ([x, y]) => (x < 20 || x > 80 ? [x + (50 - x) * 0.42, y + (50 - y) * 0.25] : y > 50 ? [x + (50 - x) * 0.25, 64] : [x + (50 - x) * 0.25, 34]);
const DEAL_STEP = C.TDA.DEAL_CARD_STEP_MS; // ms between cards, dealt one at a time round the table (server delays the first clock by the same)
const RIFFLE_MS = C.TDA.DEAL_RIFFLE_MS; // riffle clip (~1.65s) before the first card
const FLOP_STEP = 350; // 1-2-3 for the flop
// what the felt reads between streets when there is no board (draw / stud games)
const STREET_LABEL = { PREFLOP: "Pre-Flop", DRAW1: "After the 1st draw", DRAW2: "After the 2nd draw", DRAW3: "After the 3rd draw",
  THIRD: "3rd Street", FOURTH: "4th Street", FIFTH: "5th Street", SIXTH: "6th Street", SEVENTH: "7th Street" };
const streetLabel = (hand, table) => (table.family === "DRAW" && hand.street === "PREFLOP" ? "Before the draw" : hand.draw ? `Draw ${hand.draw.n}` : STREET_LABEL[hand.street] || hand.street);

// Deal order like a live table: first card to the seat after the button, one card each, round and round.
const dealOrder = (seats, button, hand) => {
  const n = seats.length;
  const inHand = seats.filter((s) => s.user_id && s.in_hand).map((s) => s.index);
  const start = button == null ? 0 : (button + 1) % n;
  return [...inHand].sort((a, b) => ((a - start + n) % n) - ((b - start + n) % n));
};

export const PokerTable = ({ clubLogoWidth, clubLogoEnds, state, skin, felt = "table_felt_green", clubLogo, serverNow, onSit, onDiscard, onDraw, dealerSpeaking, myExtra, onInfo, notes, chat = [] }) => {
  const { seats, hand, my_seat, button, sb_pos, sb_dead, bb_seat, last_result, table } = state;
  const ref = useRef(null);
  const [dims, setDims] = useState([1200, 600]);
  const ui = Math.min(1, Math.max(0.58, dims[0] / 1040)); // seats, cards and plates are fixed-px art: shrink them with the felt on small windows (laptops)
  useEffect(() => {
    preloadSounds();
    const ro = new ResizeObserver(([e]) => setDims([e.contentRect.width, e.contentRect.height]));
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  // Viewer always sees themself in Seat 3 (bottom-right, beside the action bar); everyone else rotates clockwise with them.
  const n = seats.length;
  const { slots: SLOTS, me: ME } = LAYOUTS[n] || LAYOUTS[6];
  const slotFor = (i) => SLOTS[my_seat == null ? i : (i - my_seat + ME + n) % n];
  // Winning-hand banner: anchored in px under the board (bannerTop) and set down on the opposite side of the felt
  // from the viewer's own seat, so it never covers your cards (yours are the big ones and sit higher).
  const pxDelta = (a, b) => [((a[0] - b[0]) / 100) * dims[0], ((a[1] - b[1]) / 100) * dims[1]];
  const flyFrom = (slot) => pxDelta(DEALER_AT, slot);
  const order = hand ? dealOrder(seats, button, hand) : [];
  const dealCount = table.deal_cards || table.hole_cards || 2; // cards in the opening deal (stud: 3 of 7; later streets arrive one at a time)
  const perPass = table.deal_per_pass || (dealCount <= 2 ? 1 : 2); // Hold'em and five card draw: one card per pass; PLO/stud: a pair (PLO5 2-2-1, PLO6 2-2-2)
  const openingDeal = !!hand && !hand.draws_done && (hand.street === "PREFLOP" || hand.street === "THIRD"); // after a draw / on later stud streets new cards just drop in
  // one card per pass round the table (Hold'em, five card draw) or two (PLO 4/5/6, stud: the pair lands together, 2nd a beat behind)
  const dealDelay = (seatIdx, cardIdx) => {
    if (!openingDeal || cardIdx >= dealCount) {
      // later streets (stud 4th–7th) and draw replacements: dealt round the table, one seat at a time, not all at once
      return hand ? Math.max(0, order.indexOf(seatIdx)) * DEAL_STEP : 0;
    }
    if (perPass === 1) return RIFFLE_MS + (cardIdx * order.length + order.indexOf(seatIdx)) * DEAL_STEP;
    const pass = Math.floor(cardIdx / perPass);
    return RIFFLE_MS + (pass * order.length + order.indexOf(seatIdx)) * DEAL_STEP + (cardIdx % perPass) * Math.round(DEAL_STEP * 0.35);
  };

  // sounds: riffle + one slide per card on a new hand; flip per community card (flop 1-2-3)
  const prevHand = useRef(null);
  const prevBoardLen = useRef(0);
  useEffect(() => {
    const id = hand && !hand.collecting ? hand.hand_id : null; // riffle + deal start once the deck exists (after the nonce window)
    if (id && id !== prevHand.current) {
      sounds.riffle();
      const passes = Math.ceil(dealCount / perPass); // PLO / stud: a pair per pass, one slide each
      for (let c = 0; c < passes; c++) for (let k = 0; k < order.length; k++) sounds.deal((RIFFLE_MS + (c * order.length + k) * DEAL_STEP) / 1000 + 0.25);
      prevBoardLen.current = 0;
    }
    prevHand.current = id;
    const len = hand?.board?.length || 0;
    if (len > prevBoardLen.current) {
      const added = len - prevBoardLen.current;
      for (let i = 0; i < added; i++) sounds.flip((i * FLOP_STEP) / 1000 + 0.1);
      prevBoardLen.current = len;
    }
  }, [hand?.hand_id, hand?.collecting, hand?.board?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // later streets (stud 4th–7th) and draw replacements: a slide per card as they land round the table
  const dealtCards = hand ? seats.reduce((a, s) => a + (s.in_hand && !s.folded ? s.hole?.length || 0 : 0), 0) : 0;
  const prevDealt = useRef(0);
  useEffect(() => {
    if (hand && !hand.collecting && !openingDeal && dealtCards > prevDealt.current) {
      for (let k = 0; k < dealtCards - prevDealt.current; k++) sounds.deal((k * DEAL_STEP) / 1000 + 0.05);
    }
    prevDealt.current = dealtCards;
  }, [dealtCards]); // eslint-disable-line react-hooks/exhaustive-deps

  // chips into the middle: bet/call = a few chips, raise/all-in = a bigger cascade.
  // keyed on the action count, not the street — a run-out changes the street with the same last action and must stay silent
  const laKey = hand?.last_action ? `${hand.hand_id}:${hand.action_no}` : null;
  const prevLa = useRef(undefined);
  useEffect(() => {
    if (prevLa.current !== undefined && laKey && laKey !== prevLa.current) {
      const act = hand.last_action.action;
      if (act === "ALL_IN") sounds.chipsAllIn();
      else if (act === "RAISE") sounds.chipsRaise();
      else if (act === "BET" || act === "CALL") sounds.chipsBet();
      else if (act === "FOLD") sounds.fold();
    }
    prevLa.current = laKey;
  }, [laKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // action moves to another seat
  const turnKey = hand && hand.to_act != null ? `${hand.hand_id}:${hand.street}:${hand.to_act}` : null;
  const prevTurn = useRef(undefined);
  useEffect(() => {
    if (prevTurn.current !== undefined && turnKey && turnKey !== prevTurn.current && hand.to_act === my_seat) {
      const wait = Math.max(0, hand.act_deadline - serverNow() - hand.action_timeout_ms); // first clock of the hand: chime when the deal lands
      const id = setTimeout(() => sounds.turn(true), wait);
      prevTurn.current = turnKey;
      return () => clearTimeout(id);
    }
    prevTurn.current = turnKey;
  }, [turnKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // end of street / hand: whatever was in front of each seat sweeps into the pot
  const [sweeps, setSweeps] = useState([]);
  const [pile, setPile] = useState({ amount: 0, chips: [] }); // chips already in the middle, exactly as they were bet
  const pilePending = useRef(false);

  // showdown: revealed hands sit on the table first, then the winner banner; the rake lifts out as its own figure, pauses, then the pot slides to the winner(s)
  const [payouts, setPayouts] = useState([]);
  const [rake, setRake] = useState(null);
  const [announced, setAnnounced] = useState(null); // result hand_id once the winner banner may show
  const [paid, setPaid] = useState(null); // result hand_id once the pot has left for the winner(s): the middle is empty from then on
  const [holdReveal, setHoldReveal] = useState(null); // result hand_id while showdown cards are still face-down
  const [muck, setMuck] = useState(null); // result hand_id once every card has been collected back to the dealer
  const resultKey = !hand && last_result ? last_result.hand_id : null;
  // The showdown hold is armed DURING render (React's "adjust state while rendering" pattern), not from an effect.
  // An effect runs after the first commit, so the revealed hands were painted face-up for a frame, then masked
  // face-down (replaying the 420ms flip), then turned up again: the showdown "flutter". Armed here, the very first
  // frame of a showdown is already face-down and the cards turn over exactly once when the hold is released.
  const [seenResult, setSeenResult] = useState(resultKey);
  if (resultKey !== seenResult) {
    setSeenResult(resultKey);
    if (resultKey && last_result.reason === "SHOWDOWN" && C.TDA.SHOWDOWN_HOLD_MS) setHoldReveal(resultKey);
  }
  const prevResult = useRef(undefined);
  const stale = useRef(null); // result that finished before this view existed
  useEffect(() => {
    if (resultKey && prevResult.current !== undefined && resultKey !== prevResult.current) {
      const hold = last_result.reason === "SHOWDOWN" ? C.TDA.SHOWDOWN_HOLD_MS : 0; // call announced, chips land, THEN cards turn
      if (hold) setTimeout(() => setHoldReveal((h) => (h === resultKey ? null : h)), hold); // the hold itself is armed in render, above
      const reveal = (last_result.reason === "SHOWDOWN" ? C.TDA.SHOWDOWN_REVEAL_MS : 800) + hold;
      const batch = last_result.awards.flatMap((a, ai) => a.winners.map((w) => ({ id: `${resultKey}-${ai}-${w}`, seat: w, amount: a.payouts?.[String(w)] ?? Math.floor(a.amount / a.winners.length) })));
      const hasRake = last_result.rake > 0;
      const n = last_result.reason === "SHOWDOWN" ? presentationsOf(last_result.awards).length : 1;
      // one presentation: lit ~2.6s (rake figure meanwhile); several (side pots / two boards): each shown in turn with a gap, then a pause, before the chips move
      const lit = n > 1 ? n * C.TDA.AWARD_SHOW_MS + (n - 1) * C.TDA.AWARD_GAP_MS + C.TDA.AWARD_AFTER_MS
        : last_result.reason === "SHOWDOWN" || hasRake ? C.TDA.AWARD_SINGLE_MS : 550;
      const start = reveal + lit;
      setTimeout(() => setAnnounced(resultKey), reveal);
      if (hasRake) {
        setTimeout(() => setRake({ id: resultKey, amount: last_result.rake, leaving: false }), n > 1 ? start - C.TDA.AWARD_SINGLE_MS : reveal);
        setTimeout(() => setRake((r) => (r && r.id === resultKey ? { ...r, leaving: true } : r)), start - 500);
        setTimeout(() => setRake((r) => (r && r.id === resultKey ? null : r)), start + 200);
      }
      setTimeout(() => { setPayouts((p) => [...p, ...batch]); setPaid(resultKey); sounds.win(); }, start);
      setTimeout(() => setPayouts((p) => p.filter((x) => !batch.includes(x))), start + 3400);
      // THE MUCK (every game, every seat): the moment the winner is paid, every card still on the felt turns
      // face-down and is collected back to the dealer — including the showdown hands that had been lifted out.
      setTimeout(() => setMuck(resultKey), start);
    } else if (resultKey && prevResult.current === undefined) {
      stale.current = resultKey; // joined/returned between hands: the finished hand is history — present a clean table
    }
    prevResult.current = resultKey;
  }, [resultKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const showBanner = !hand && last_result && announced === last_result.hand_id;
  const settling = !!resultKey && announced !== resultKey && stale.current !== resultKey; // final-street bets stay on the felt until the winner is announced
  const clean = !hand && (!last_result || stale.current === last_result.hand_id); // nothing from a hand this viewer never saw
  // live bets: the last street's chips (incl. the closing call) stay in front of the seats through the reveal, then sweep into the pot
  // stud: the antes sit in front of every player until the deal, then sweep into the pot
  const studAnte = !!hand && table.family === "STUD" && hand.collecting && hand.ante > 0;
  const bets = seats.map((s) => (studAnte ? (s.in_hand ? hand.ante : 0) : hand || settling ? s.bet || 0 : 0));
  const prevBets = useRef(bets);
  const streetKey = `${hand?.hand_id ?? "none"}:${hand?.street ?? ""}:${settling ? "settle" : "done"}`;
  // uncalled bet portion (e.g. the BB's extra over a short all-in SB): the server hands it back the moment betting is
  // complete; those chips slide from the bet spot back to the seat, and the later sweep only carries what stayed in
  const [returns, setReturns] = useState([]);
  const returned = useRef(new Set());
  const ret = hand?.uncalled || (settling ? last_result?.uncalled : null) || null;
  const retKey = ret ? `${hand?.hand_id ?? last_result?.hand_id}:${ret.street}:${ret.seat}` : null;
  useEffect(() => {
    if (!retKey || returned.current.has(retKey) || stale.current === last_result?.hand_id) return;
    returned.current.add(retKey);
    prevBets.current = prevBets.current.map((b, i) => (i === ret.seat ? Math.max(0, b - ret.amount) : b));
    const item = { id: retKey, seat: ret.seat, amount: ret.amount };
    setReturns((r) => [...r, item]);
    sounds.chipsBet();
    setTimeout(() => setReturns((r) => r.filter((x) => x !== item)), 2900);
  }, [retKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const gone = prevBets.current.map((b, i) => ({ index: i, bet: b })).filter((p) => p.bet > 0 && !(bets[p.index] > 0));
    if (gone.length) {
      const batch = gone.map((p) => ({ id: `${streetKey}-${p.index}`, seat: p.index, amount: p.bet }));
      setSweeps((s) => [...s, ...batch]);
      setTimeout(() => setSweeps((s) => s.filter((x) => !batch.includes(x))), 700);
      // pot pile: the wagered chips themselves (no re-denomination) land on the pile as the sweep arrives
      pilePending.current = true;
      setTimeout(() => { pilePending.current = false; setPile((p) => ({ amount: p.amount + batch.reduce((a, w) => a + w.amount, 0), chips: [...p.chips, ...batch.flatMap((w) => chipStack(w.amount, 8))].slice(-48) })); }, 600);
    }
  }, [streetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { prevBets.current = bets; });

  const board = hand?.board || (clean ? [] : last_result?.board) || [];
  const board2 = hand?.board2 || (clean ? [] : last_result?.board2) || [];
  const bomb = !!(hand ? hand.bomb : !clean && last_result?.bomb_pot); // DOUBLE BOARD BOMB POT: two boards, 15% smaller
  // RUN IT TWICE: heads-up all-in and both agreed — the first run sits on top, the second run below it
  const rit = !!(hand ? hand.rit : !clean && last_result?.run_it_twice);
  const two = bomb || rit; // two boards on the felt
  // run it twice: hold a beat on "RUNNING IT TWICE" before run one, and again on "RUN 2" before the second run
  const ritFrom = hand?.rit_from ?? (!clean && last_result?.rit_from) ?? 0;
  // burned cards sit face down beside the chip rack: a count while the hand runs, the published list afterwards
  const burnCount = hand ? hand.burns || 0 : (!clean && last_result?.burns?.length) || 0;
  const ritAnnounce = !!(hand?.rit && hand.board.length === ritFrom);
  const runTwoStart = !!(hand?.rit && hand.board.length >= 5 && hand.board2.length === ritFrom);
  useEffect(() => { if (ritAnnounce) sounds.riffle(); }, [ritAnnounce]);
  const boardDelay = (i) => (board.length <= 3 ? i * FLOP_STEP : 0); // flop 1-2-3, turn/river drop straight in
  // bomb pot: the antes land with a cascade as the burst pops; the burst itself is gone after ~4s
  const [burst, setBurst] = useState(null);
  useEffect(() => {
    if (!hand?.bomb || hand.board.length) return undefined; // only as the bomb hand opens (not when joining mid-hand)
    sounds.chipsAllIn();
    setBurst(hand.hand_id);
    const id = setTimeout(() => setBurst((b) => (b === hand.hand_id ? null : b)), BURST_MS);
    return () => clearTimeout(id);
  }, [hand?.bomb ? hand.hand_id : null]); // eslint-disable-line react-hooks/exhaustive-deps
  // draw games: the viewer's picks for this draw (tap to raise a card); cleared whenever a new draw opens
  const [picked, setPicked] = useState(() => new Set());
  const drawKey = hand?.draw ? `${hand.hand_id}:${hand.draw.n}` : null;
  useEffect(() => { setPicked(new Set()); }, [drawKey]);
  const myDraw = hand?.draw?.pending?.includes(my_seat) ? {
    picked, deadline: hand.draw.deadline, n: hand.draw.n,
    toggle: (c) => setPicked((p) => { const q = new Set(p); if (q.has(c)) q.delete(c); else q.add(c); return q; }),
    confirm: (cards) => onDraw?.(cards),
  } : undefined;
  // where a seat's chips land; with two boards the bottom seats' chips drop below the lower board
  const betAt = (slot) => { const [x, y] = slot; return two && x >= 20 && x <= 80 && y > 50 ? [x + (50 - x) * 0.5, 80] : betPos(slot); };
  // plate shows the full pot (winnings + rake) until the chips have left for the winner; main / side pots sit side by side
  const pot = hand ? hand.pot : last_result && paid !== resultKey ? last_result.awards.reduce((s, a) => s + a.amount, 0) + (payouts.length ? 0 : last_result.rake || 0) : 0;
  const potsNow = hand ? hand.pots || [] : last_result && paid !== resultKey
    ? [...new Set(last_result.awards.map((a) => a.pot ?? 0))].map((k) => ({ amount: last_result.awards.filter((a) => (a.pot ?? 0) === k).reduce((s, a) => s + a.amount, 0) + (k === 0 && !payouts.length ? last_result.rake || 0 : 0) }))
    : [];
  const plates = potsNow.length > 1 ? potsNow : [{ amount: pot }];
  // chips already in the middle (everything except the live bets still in front of the seats); gone once the payout slides out
  const collected = payouts.length ? 0 : Math.max(0, pot - bets.reduce((a, b) => a + b, 0));
  // the pile is cleared the moment the pot leaves for the winner (or a new hand starts); it is fed by the sweep effect above
  useEffect(() => {
    if (collected === 0) setPile({ amount: 0, chips: [] });
    else if (pile.amount === 0 && !pilePending.current) setPile({ amount: collected, chips: chipStack(collected, 16) }); // arrived mid-hand: show what's already in the middle
  }, [collected]); // eslint-disable-line react-hooks/exhaustive-deps
  const posOf = (i) => (hand?.bomb ? (i === button ? "D" : null) // bomb pot: antes only, no blinds
    : i === bb_seat ? "BB" : i === sb_pos && !sb_dead ? (i === button ? "D/SB" : "SB") : i === button ? "D" : null);
  const target = hand?.to_act != null ? slotFor(hand.to_act) : null;
  // showdown: pots are presented in turn — MAIN first, then each side pot; in a bomb pot a pot whose boards went to different
  // players is shown twice (TOP, then BOTTOM), a scoop once with both boards lit. The five cards that make each winning hand
  // light up, everything else dims; the winners' hole cards that play lift out of the seat to sit by the board.
  const awards = showBanner && last_result.reason === "SHOWDOWN" ? last_result.awards : [];
  const groups = presentationsOf(awards);
  const [revealIdx, setRevealIdx] = useState(0); // presentation on show; −i = the dark gap just before presentation i
  useEffect(() => {
    setRevealIdx(0);
    if (!showBanner || groups.length < 2) return;
    const ids = []; // presentation i lights at i × (show + gap); the gap before it goes dark so the eye registers the change
    for (let i = 1; i < groups.length; i++) {
      const at = i * (C.TDA.AWARD_SHOW_MS + C.TDA.AWARD_GAP_MS);
      ids.push(setTimeout(() => setRevealIdx(-i), at - C.TDA.AWARD_GAP_MS), setTimeout(() => setRevealIdx(i), at));
    }
    return () => ids.forEach(clearTimeout);
  }, [showBanner, resultKey, groups.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // same winner(s) taking the next pot with the same cards: hold the lift through the gap — the cards go up once and stay
  const shownIdx = revealIdx >= 0 ? revealIdx : liftKeyOf(groups[-revealIdx - 1]) === liftKeyOf(groups[-revealIdx]) ? -revealIdx - 1 : null;
  const group = groups.length && holdReveal !== resultKey && shownIdx != null ? groups[Math.min(shownIdx, groups.length - 1)] : null;
  const curBoard = group && group.length === 1 && group[0].board ? group[0].board : null; // one board of a bomb pot; null = everything in the group lit
  const winners = group ? [...new Set(group.flatMap((a) => a.winners))] : [];
  const cardsOf = (attr) => new Set(winners.flatMap((w) => seats[w]?.[attr]?.cards || []));
  const winTop = group && curBoard !== "BOTTOM" ? cardsOf("hand_rank") : new Set();
  const winBottom = group && two && curBoard !== "TOP" ? cardsOf("hand_rank2") : new Set();
  const winning = group ? new Set([...winTop, ...winBottom]) : null;
  const liveWin = !!(winning && winning.size) && muck !== resultKey; // the muck takes the lifted winning cards away too
  const boardWinning = (row) => (liveWin ? (row === 0 ? winTop : winBottom) : null); // the board not being presented dims
  // a card shared by both runs is lit if it plays on either board (row === null)
  const winOn = (row) => (liveWin ? (row === null ? winning : boardWinning(row)) : new Set());
  // the cards that lift out of a winner's seat read in hand order (straight high to low, trips before the pair, …)
  const lifted = liveWin ? winners.map((w) => ({ seat: w, cards: orderHand((seats[w]?.hole || []).filter((c) => winning.has(c))) })).filter((x) => x.cards.length) : [];
  const liftedFor = (i) => { const x = lifted.find((l) => l.seat === i); return x ? new Set(x.cards) : null; };
  const boardCard = { w: two ? 69 : 81, h: two ? 102 : 120 };
  const BOARD_TOP = two ? "39%" : "42%";
  // Hangs just above the viewer's own hole cards (the big ones, which sit higher than everyone else's) — both the cards
  // and this banner are fixed-px art scaled by `ui`, so the gap holds on every screen size.
  return (
    <div ref={ref} data-testid="poker-table" className="relative w-full" style={{ aspectRatio: "2 / 1" }}>
      <DealerPortrait skin={skin} target={target} speaking={dealerSpeaking} />
      <AssetImage id={felt} className="absolute inset-0 w-full h-full" style={{ filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.65))" }} />
      {clubLogo && (clubLogoEnds ? ["25%", "75%"] : ["50%"]).map((x, n) => (
        <img key={x} src={clubLogo} alt="" aria-hidden data-testid={n ? `table-club-logo-${n}` : "table-club-logo"}
          className="absolute top-[48%] -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none z-0"
          style={{ left: x, width: clubLogoWidth || "44%", opacity: 0.85, filter: "drop-shadow(0 3px 14px rgba(0,0,0,0.55))" }} />
      ))}
      {/* pot plates: a lone pot reads "Pot"; once a side pot exists they sit in a row — MAIN, SP1, SP2 … (the chip pile stays under MAIN) */}
      <div data-testid="pot-display" className="absolute top-[32%] -translate-y-1/2 flex items-center gap-2 pointer-events-none" style={{ left: "calc(50% - 56px)" }}>
        {plates.map((p, i) => (
          <div key={i} data-testid={i === 0 ? "pot-main" : `pot-side-${i}`} className="relative flex items-center justify-center font-mono text-sm text-amber-100 pot-plate" style={{ width: 112, height: 75 }}>
            <AssetImage id="pot_display" width={112} className="absolute inset-0 drop-shadow-lg" />
            <span className="relative z-10 drop-shadow font-semibold flex flex-col items-center leading-tight">
              {plates.length > 1 && <span className="text-[9px] tracking-[0.25em] text-amber-300/90">{i === 0 ? "MAIN" : `SP${i}`}</span>}
              <span>{plates.length > 1 ? fmtChips(p.amount) : `Pot ${fmtChips(pot)}`}</span>
            </span>
          </div>
        ))}
      </div>
      {pile.amount > 0 && <PotChips amount={pile.amount} chips={pile.chips.length ? pile.chips : chipStack(pile.amount, 8)} />}
      {rake && (
        <div data-testid="rake-figure" className={`absolute flex items-center gap-1.5 z-20 pointer-events-none ${rake.leaving ? "rake-out" : "rake-in"}`} style={{ left: `calc(50% + ${70 + 120 * (plates.length - 1)}px)`, top: `${POT_AT[1]}%`, transform: "translateY(-50%)" }}>
          <AssetImage id={chipAssetId(rake.amount)} width={26} className="drop-shadow" />
          <span className="font-mono text-xs bg-slate-900/85 border border-slate-500/40 rounded-full px-2 py-0.5 text-slate-200 whitespace-nowrap">− {fmtChips(rake.amount)} rake</span>
        </div>
      )}
      {/* burn cards: face down on the wood beside the chip rack, one more each time a street is dealt */}
      {burnCount > 0 && (
        <div data-testid="burn-cards" className="absolute flex items-end pointer-events-none z-[6]"
          style={{ top: "15%", left: "calc(50% + 90px)", transform: `scale(${ui})`, transformOrigin: "0 50%" }}>
          {Array.from({ length: burnCount }, (_, i) => (
            <div key={i} style={{ marginLeft: i ? -16 : 0, transform: `rotate(${(i % 2 ? 1 : -1) * (2 + i)}deg)` }} data-testid={`burn-card-${i}`}>
              <CardView card="XX" size="burn" />
            </div>
          ))}
        </div>
      )}
      {state.tournament && !hand && !last_result && (
        <div className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 z-10" style={{ perspective: 900 }}>
          <AssetImage id="sng_wait_coin" width={150} className="sng-coin drop-shadow-[0_12px_28px_rgba(0,0,0,0.65)]" data-testid="sng-wait-coin" />
        </div>
      )}
      <div className="absolute left-1/2 w-max pointer-events-none" style={{ top: BOARD_TOP, transform: `translateX(-50%) scale(${ui})`, transformOrigin: "50% 0" }}>
        {/* the winner's hole cards that play lift out of the seat: above the single board, or beside the two boards of a bomb pot */}
        {/* winners' playing cards lift out of the seat (slow, visible arc). One winner: centred above the board (beside it in a
            bomb pot). A split: each winner's cards go to opposite ends — left end / right end — with a name tag, so whose is whose is obvious. */}
        {lifted.map((l, i) => {
          const many = lifted.length > 1;
          const pos = two
            ? (i % 2 === 0 ? "left-full top-1/2 ml-3" : "right-full top-1/2 mr-3")
            : many ? (i % 2 === 0 ? "left-0 bottom-full mb-1.5" : "right-0 bottom-full mb-1.5") : "left-1/2 bottom-full mb-1.5";
          const origin = two ? (i % 2 === 0 ? "left center" : "right center") : many ? (i % 2 === 0 ? "bottom left" : "bottom right") : "bottom center";
          const tx = two ? "translateY(-50%)" : many ? "" : "translateX(-50%)";
          return (
            <div key={`${resultKey}-${l.seat}-${l.cards.join()}`} data-testid={`winner-hole-cards-${l.seat}`} className={`absolute w-max flex flex-col items-center gap-1 ${pos}`}
              style={{ transform: `${tx} scale(0.8)`.trim(), transformOrigin: origin, top: i >= 2 && !two ? "-130px" : undefined }}>
              <div className="flex gap-1.5">
                {l.cards.map((c, k) => <CardView key={`${resultKey}-${c}`} card={c} size="lg" delay={k * 140} slow from={pxDelta(slotFor(l.seat), two ? (i % 2 === 0 ? [66, 50] : [34, 50]) : many ? (i % 2 === 0 ? [38, 30] : [62, 30]) : [50, 30])} highlight="win" />)}
              </div>
              {many && <span data-testid={`winner-tag-${l.seat}`} className="winner-tag text-[11px] font-display tracking-wider text-amber-100 bg-slate-900/85 border border-amber-400/50 rounded-full px-3 py-0.5 whitespace-nowrap">{seats[l.seat]?.name}</span>}
            </div>
          );
        })}
        {lifted.length > 0 && <span data-testid="winner-hole-cards" className="hidden" />}
        {bomb && (hand || last_result) && (
          <div data-testid="bomb-pot-tag" className="bomb-tag absolute right-full top-1/2 -translate-y-1/2 mr-3 rounded-md border border-amber-400/70 bg-gradient-to-b from-orange-600 to-red-800 px-2 py-1 text-center">
            <div className="font-display text-[10px] tracking-[0.2em] text-amber-100 leading-tight">BOMB POT</div>
            <div className="text-[8px] uppercase tracking-wider text-amber-200/90 leading-tight">½ pot max</div>
          </div>
        )}
        {rit && (hand || last_result) && (
          <div data-testid="run-it-twice-tag" className="absolute right-full top-1/2 -translate-y-1/2 mr-3 rounded-md border border-emerald-300/70 bg-gradient-to-b from-emerald-600 to-emerald-900 px-2 py-1 text-center">
            <div className="font-display text-[10px] tracking-[0.2em] text-emerald-50 leading-tight">RUN TWICE</div>
          </div>
        )}
        {(() => {
          if (table.family && table.family !== "FLOP") return null;
          const slot = (card, i, r, tid) => (
            <div key={tid} data-testid={tid} className="rounded-md border border-amber-100/15 bg-black/10 transition-transform duration-500"
              style={{ width: boardCard.w, height: boardCard.h, transform: liveWin && card && winOn(r).has(card) ? "translateY(-14px)" : undefined }}>
              {card && <CardView card={card} size={two ? "bomb" : "lg"} delay={boardDelay(i)} from={flyFrom([50, 52])} highlight={!hand && liveWin ? (winOn(r).has(card) ? "win" : "dim") : undefined} />}
            </div>
          );
          const label = (r) => rit && (
            <span data-testid={`run-label-${r + 1}`} className="absolute left-full ml-2 font-display text-[10px] tracking-[0.2em] text-emerald-200/90 whitespace-nowrap">{r === 0 ? "TOP BOARD" : "BOTTOM BOARD"}</span>
          );
          // ONE board row that never unmounts: the shared cards keep their place (they only shift up when a second
          // row appears) and a hand run twice adds the bottom row with just its new cards, aligned under the top ones
          const gap = two ? 4 : 8;
          const from2 = rit ? ritFrom : 0;
          return (
            <div className="flex flex-col" style={{ gap }}>
              <div data-testid="community-cards" className="relative flex items-center" style={{ gap }}>
                {label(0)}
                {[0, 1, 2, 3, 4].map((i) => slot(board[i], i, rit && i < ritFrom ? null : 0, `board-slot-${i}`))}
              </div>
              {two && (
                <div data-testid="community-cards-bottom" className="relative flex items-center"
                  style={{ gap, marginLeft: from2 * (boardCard.w + gap) }}>
                  {label(1)}
                  {Array.from({ length: 5 - from2 }, (_, k) => slot(board2[from2 + k], from2 + k, 1, `board2-slot-${from2 + k}`))}
                </div>
              )}
            </div>
          );
        })()}
        {/* anything below the board is absolutely positioned so the board itself never moves */}
        <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+8px)] w-max flex flex-col items-center gap-2">
          {board.length === 0 && !(hand?.bomb) && <span data-testid="street-label" className="text-sm uppercase tracking-[0.35em] text-amber-100/70 glass rounded-md px-6 py-2">{hand ? streetLabel(hand, table) : `${table.stakes} · ${C.GAME_RULES[table.game]?.label || table.game}`}</span>}
          {!hand && !last_result && !state.tournament && <div data-testid="waiting-notice" className="text-xs text-emerald-100/60 uppercase tracking-[0.3em]">Waiting for players</div>}
          {state.bomb_next && !hand?.bomb && <div data-testid="bomb-next-notice" className="bomb-tag text-[10px] font-display tracking-[0.25em] text-amber-100 rounded-full border border-amber-400/70 bg-red-800/90 px-4 py-1">NEXT HAND: BOMB POT</div>}
        </div>
      </div>
      {/* run it twice: the announcement that builds the moment, then the call for the second run */}
      {(ritAnnounce || runTwoStart) && (
        <div data-testid="rit-announce" className="absolute left-1/2 top-[14%] z-30 pointer-events-none" style={{ transform: "translate(-50%, -50%)" }}>
          <div className="rounded-2xl border-2 border-emerald-300/80 bg-slate-950/90 px-10 py-4 text-center shadow-[0_0_50px_rgba(16,185,129,0.55)] rise">
            <div className="font-display text-2xl tracking-[0.3em] text-emerald-100">{ritAnnounce ? "RUNNING IT TWICE" : "RUN 2"}</div>
          </div>
        </div>
      )}
      {/* bomb pot: the burst sits over the community-card area while the antes are taken and the cards dealt */}
      {burst && hand?.hand_id === burst && (
        <div className="bomb-burst-wrap absolute left-1/2 top-[46%] z-20 pointer-events-none" style={{ transform: "translate(-50%, -50%)" }}>
          <BombPotBurst width={Math.min(380, dims[0] * 0.32)} />
        </div>
      )}
      {/* result banner lives low-centre, between the bottom seats, so showdown cards never cover it; when the viewer's seat is bottom-centre it moves to the free left-hand side */}
      {showBanner && <WinnerPills result={last_result} seats={seats} current={group} slotFor={slotFor} scale={ui} />}
      {seats.map((s) => (
        <SeatView key={s.index} seat={s} slot={slotFor(s.index)} meX={my_seat != null ? slotFor(my_seat)[0] : null} isMe={s.index === my_seat} position={posOf(s.index)} blinds={[table.sb, table.bb]} scale={ui} sng={!!state.tournament} extra={s.index === my_seat ? myExtra : null} settling={settling} handKey={hand?.hand_id ?? last_result?.hand_id}
          isActing={hand?.to_act === s.index} hand={hand} serverNow={serverNow} onSit={onSit} canSit={my_seat == null} onInfo={onInfo} noteColor={notes?.[s.user_id]} clean={clean} champion={!!s.user_id && s.user_id === state.champion_id}
          onDiscard={s.index === my_seat && hand?.discard?.pending?.includes(my_seat) ? onDiscard : undefined} discardDeadline={hand?.discard?.deadline} draw={s.index === my_seat ? myDraw : undefined}
          handLabel={s.index === my_seat ? state.my_hand?.label : null} holdReveal={holdReveal === resultKey} lifted={liftedFor(s.index)} muck={!!resultKey && muck === resultKey}
          dealFrom={flyFrom(slotFor(s.index))} dealDelay={(cardIdx) => dealDelay(s.index, cardIdx)} winning={liveWin ? winning : null} />
      ))}
      <ChatBubbles chat={chat} me={my_seat} at={(i) => { const [x, y] = slotFor(i); return { left: `${x}%`, top: `${y}%` }; }} />
      {seats.map((s) => bets[s.index] > 0 && (
        <BetChips key={`${s.index}-${bets[s.index]}`} amount={bets[s.index]} at={betAt(slotFor(s.index))} from={pxDelta(slotFor(s.index), betAt(slotFor(s.index)))} testId={`seat-${s.index}-bet`}
          badge={studAnte ? "ANTE" : hand?.street === "PREFLOP" && !hand.bomb ? (posOf(s.index) === "BB" && s.bet <= table.bb ? "BB" : posOf(s.index)?.includes("SB") && s.bet <= table.sb ? "SB" : null) : null} />
      ))}
      {returns.map((w) => (
        <BetChips key={w.id} amount={w.amount} at={betAt(slotFor(w.seat))} to={pxDelta(slotFor(w.seat), betAt(slotFor(w.seat)))} slow badge="↩" testId={`seat-${w.seat}-bet-return`} />
      ))}
      {sweeps.map((w) => (
        <BetChips key={w.id} amount={w.amount} at={betAt(slotFor(w.seat))} to={pxDelta(POT_AT, betAt(slotFor(w.seat)))} testId={`seat-${w.seat}-bet-sweep`} />
      ))}
      {payouts.map((w) => (
        <BetChips key={w.id} amount={w.amount} at={POT_AT} to={pxDelta(slotFor(w.seat), POT_AT)} slow big testId={`seat-${w.seat}-payout`} />
      ))}
    </div>
  );
};
